import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

export const PromoBanner = () => {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api
      .get("/banners")
      .then(({ data }) => setBanners(data))
      .catch(() => setBanners([]));
  }, []);

  const next = useCallback(() => {
    setIdx((v) => (banners.length ? (v + 1) % banners.length : 0));
  }, [banners.length]);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(next, 5500);
    return () => clearInterval(t);
  }, [banners.length, next]);

  if (!banners.length) return null;
  const b = banners[idx];

  const onClick = (link) => {
    if (!link) return;
    if (link.startsWith("#")) {
      document.querySelector(link)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.open(link, "_blank", "noopener");
    }
  };

  return (
    <section className="py-10 lg:py-14 bg-background" data-testid="promo-banner-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative overflow-hidden bg-foreground aspect-[16/6] min-h-[260px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={b.id}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
              data-testid={`promo-slide-${idx}`}
            >
              <img
                src={fileUrl(b.image_path)}
                alt={b.title || "Promotion"}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/35 to-transparent" />
              <div className="absolute inset-0 flex items-center">
                <div className="px-8 sm:px-14 max-w-xl">
                  {b.title && (
                    <motion.h3
                      initial={{ y: 24, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.7 }}
                      className="font-display text-4xl sm:text-6xl leading-none tracking-tight text-white"
                      data-testid="promo-title"
                    >
                      {b.title}
                    </motion.h3>
                  )}
                  {b.subtitle && (
                    <motion.p
                      initial={{ y: 24, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.32, duration: 0.7 }}
                      className="mt-4 text-white/80 text-base sm:text-lg max-w-md"
                    >
                      {b.subtitle}
                    </motion.p>
                  )}
                  {b.button_text && (
                    <motion.button
                      initial={{ y: 24, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.44, duration: 0.7 }}
                      onClick={() => onClick(b.button_link)}
                      className="mt-7 inline-flex items-center gap-3 bg-accent text-white px-7 py-3.5 text-xs font-mono-label uppercase tracking-widest hover:bg-white hover:text-foreground transition-colors duration-300"
                      data-testid="promo-cta"
                    >
                      {b.button_text}
                      <ArrowRight size={15} />
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {banners.length > 1 && (
            <div className="absolute bottom-5 right-6 flex gap-2 z-10">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === idx ? "w-7 bg-white" : "w-2.5 bg-white/40"
                  }`}
                  data-testid={`promo-dot-${i}`}
                  aria-label={`Promo ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
